import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { InvitationToken } from '@/types/team';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ROUTES } from '@/config/routes';
import { useTranslation } from 'react-i18next';
import { logError } from '@/utils/errorHandler';

interface Membership {
  id: string;
  user?: {
    email: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  role?: string;
  is_new_user?: boolean;
  user_exists?: boolean;
  needs_password?: boolean;
}

interface ApiError {
  response?: {
    data?: {
      detail?: string;
    };
  };
}

const AcceptInvitePage: React.FC = () => {
  const { t } = useTranslation('translation');
  const { membershipId, token } = useParams<{
    membershipId: string;
    token: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkMembership = async () => {
      try {
        setLoading(true);

        // Fetch membership details if possible to check if this is a valid invitation
        if (membershipId) {
          try {
            const response = await api.get(`/api/orgs/memberships/${membershipId}/check/`, {
              // Allow unauthenticated check
              validateStatus: status => status < 500,
            });

            if (response.status === 200) {
              setMembership(response.data);
              setInvitedEmail(response.data.user?.email);
            }
          } catch (err) {
            logError(err instanceof Error ? err : 'Error checking membership', {
              component: 'AcceptInvitePage',
              action: 'checkMembership',
              metadata: { membershipId },
            });
            // Continue with the flow even if we can't check the membership
          }
        }

        // First, check if the user is logged in
        if (!isAuthenticated) {
          // Store the invitation details to process after login
          const pendingInvitation: InvitationToken = {
            membershipId: membershipId || '',
            token: token || '',
          };

          sessionStorage.setItem('pendingInvitation', JSON.stringify(pendingInvitation));

          // If we know the invited email and it doesn't match any existing user
          // redirect to set password page instead of registration
          if (invitedEmail && membership?.is_new_user && membership.organization?.id) {
            navigate(
              `${ROUTES.AUTH.SET_PASSWORD.replace(':orgId', membership.organization.id)}?token=${token}&email=${invitedEmail}`
            );
            return;
          }

          // Redirect to login
          navigate(`${ROUTES.AUTH.LOGIN}?redirect=invitation`);
          return;
        }

        // Check if the logged-in user is the intended recipient
        if (invitedEmail && user?.email !== invitedEmail) {
          setError(
            `This invitation was sent to ${invitedEmail}, but you're logged in as ${user?.email}. Please log out and log in with the correct account.`
          );
          setLoading(false);
          return;
        }

        // If user exists but doesn't have a password set (user was created when invited)
        if (
          membership?.user_exists &&
          membership?.needs_password &&
          invitedEmail &&
          membership.organization?.id
        ) {
          // User exists but needs to set a password - redirect to set-password page
          navigate(
            `${ROUTES.AUTH.SET_PASSWORD.replace(':orgId', membership.organization.id)}?token=${token}&email=${encodeURIComponent(invitedEmail)}`
          );
          return;
        }

        // Call the API to accept the invitation
        await api.post(`/api/orgs/memberships/${membershipId}/accept/`, {
          token: token,
        });

        setSuccess(true);
        setError(null);

        // After 3 seconds, redirect to dashboard
        setTimeout(() => {
          navigate(ROUTES.APP.ROOT);
        }, 3000);
      } catch (err: unknown) {
        logError(err instanceof Error ? err : 'Error accepting invitation', {
          component: 'AcceptInvitePage',
          action: 'acceptInvitation',
          metadata: { membershipId, token },
        });
        setError(
          (err as ApiError).response?.data?.detail ||
            'An error occurred while accepting the invitation'
        );
      } finally {
        setLoading(false);
      }
    };

    if (membershipId && token) {
      checkMembership();
    }
  }, [membershipId, token, isAuthenticated, navigate, user, invitedEmail, membership]);

  const handleRedirect = () => {
    // If the user needs to set a password (new user), redirect to the set password page
    if (membership?.is_new_user && membership.organization?.id && membership.user?.email) {
      navigate(
        `${ROUTES.AUTH.SET_PASSWORD.replace(':orgId', membership.organization.id)}?token=${membershipId}&email=${membership.user.email}`
      );
    } else {
      // Otherwise, redirect to login
      navigate(`${ROUTES.AUTH.LOGIN}?invitation=${membershipId}`);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto size-8 animate-spin text-blue-600" />
          <h2 className="mt-4 text-xl font-medium text-gray-900">Checking invitation...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-fit rounded-full bg-red-100 p-3">
              <AlertCircle className="size-8 text-red-600" />
            </div>
            <CardTitle className="mt-3 text-xl">Invalid Invitation</CardTitle>
            <CardDescription className="text-red-600">{error}</CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate(ROUTES.ROOT)}>
              {t('marketing.invitation.error.buttons.returnHome', 'Return to Home')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-fit rounded-full bg-green-100 p-3">
              <CheckCircle className="size-8 text-green-600" />
            </div>
            <CardTitle className="mt-3 text-xl">Already Accepted</CardTitle>
            <CardDescription>This invitation has already been accepted</CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate(ROUTES.AUTH.LOGIN)}>
              {t('auth.login.form.buttons.signIn', 'Log In')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-xl">You've Been Invited</CardTitle>
          <CardDescription>
            {membership?.organization?.name || 'An organization'} has invited you to join their team
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-md bg-gray-50 p-4">
              <p className="text-sm text-gray-700">
                <strong>Email:</strong> {membership?.user?.email}
              </p>
              {membership?.role && (
                <p className="mt-2 text-sm text-gray-700">
                  <strong>Role:</strong> {membership.role}
                </p>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-4">
          <Button variant="outline" onClick={() => navigate(ROUTES.ROOT)}>
            Cancel
          </Button>
          <Button onClick={handleRedirect}>
            {membership?.is_new_user ? 'Set Up Account' : 'Accept Invitation'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default AcceptInvitePage;
